import { type ReportView, api } from './api.js'
import { esc, money, money2, monthLong, shortDate } from './format.js'

/**
 * The monthly report for the accountant.
 *
 * The disclaimer appears at the top and the bottom of every copy, as the
 * project brief requires — on screen and in every export.
 */

const $ = <T extends HTMLElement>(selector: string): T =>
  document.querySelector<T>(selector) as T

let reportMonth: string | null = null

export function openReport(month: string): void {
  reportMonth = month
  const dialog = $<HTMLDialogElement>('#rpt')
  dialog.showModal()
  void loadReport()
}

export function closeReport(): void {
  $<HTMLDialogElement>('#rpt').close()
}

export function stepReportMonth(delta: number): void {
  if (!reportMonth) return
  const year = Number(reportMonth.slice(0, 4))
  const index = Number(reportMonth.slice(5, 7)) - 1 + delta
  const shifted = new Date(Date.UTC(year, index, 1))
  reportMonth = `${shifted.getUTCFullYear()}-${String(shifted.getUTCMonth() + 1).padStart(2, '0')}`
  void loadReport()
}

export function currentReportMonth(): string | null {
  return reportMonth
}

async function loadReport(): Promise<void> {
  if (!reportMonth) return

  $('#rptMonth').textContent = monthLong(reportMonth)
  $('#rptErr').textContent = ''
  $('#rptBody').innerHTML = '<p class="note">Building the report…</p>'

  try {
    render(await api.report(reportMonth))
  } catch (error) {
    $('#rptBody').innerHTML = ''
    $('#rptErr').textContent =
      error instanceof Error ? error.message : 'Could not build the report.'
  }
}

function render(report: ReportView): void {
  const s = report.summary

  const line = (label: string, value: string, strong = false) =>
    `<div class="rline${strong ? ' tot' : ''}"><span>${esc(label)}</span><b class="num">${money(
      value,
    )}</b></div>`

  const heading = (text: string) => `<h3 class="rhead">${esc(text)}</h3>`

  const openSection = (
    title: string,
    items: ReportView['openItems']['unpaidBills'],
    total: string,
  ) =>
    items.length
      ? `${heading(`${title} (${items.length}) — ${money(total)}`)}
         <ul class="rlist">${items
           .map(
             (item) =>
               `<li><span class="d num">${shortDate(item.date)}</span>
                <span>${esc(item.description)}${
                  item.detail ? ` <span class="note">— ${esc(item.detail)}</span>` : ''
                }</span>
                <b class="num">${money2(item.amount)}</b></li>`,
           )
           .join('')}</ul>`
      : ''

  const warnings: string[] = []
  if (report.containsSampleData) {
    warnings.push(
      '<div class="banner"><span><b>This report still includes sample data.</b> Clear it before sending.</span></div>',
    )
  }
  if (report.openItems.unresolvedAmazon.length > 0) {
    warnings.push(
      `<div class="banner"><span><b>${report.openItems.unresolvedAmazon.length} Amazon purchase(s) are not resolved.</b> They are counted, but not yet explained.</span></div>`,
    )
  }

  $('#rptBody').innerHTML = `
    <p class="disclaimer">${esc(report.disclaimer)}</p>
    ${warnings.join('')}

    ${heading('Revenue')}
    ${line('Recurring Monthly Revenue', s.recurringMonthlyRevenue)}
    ${line('Recurring Revenue Received', s.recurringReceived)}
    ${line(
      s.recurringOutstanding.startsWith('-')
        ? 'Recurring received above base'
        : 'Recurring Revenue Outstanding',
      s.recurringOutstanding.replace('-', ''),
    )}
    ${line('Additional Revenue Received', s.additionalReceived)}
    ${line('Total Revenue Received', s.totalRevenueReceived, true)}

    ${heading('Outflows')}
    ${line('Bills paid', s.billsPaid)}
    ${line('Regular Spending (RS)', s.regularSpending)}
    ${line('Payroll & Owner Pay (scheduled)', s.payrollScheduled)}
    ${line('Processing / card fees', s.processingFees)}
    ${line('Investigate (?) — not yet identified', s.investigate)}
    ${line('Total spending / outflows', s.totalOutflows, true)}

    ${heading('Cash')}
    ${line('Cushion at start of month', s.cushionStart)}
    ${line('Remaining cash / cushion', s.remainingCash, true)}
    ${
      s.transfers !== '0.00'
        ? `<p class="note">${money(
            s.transfers,
          )} in transfers between NI accounts is excluded from every figure above.</p>`
        : ''
    }

    ${openSection('Unresolved Amazon purchases', report.openItems.unresolvedAmazon, report.openItems.unresolvedAmazonTotal)}
    ${openSection('Unpaid bills', report.openItems.unpaidBills, report.openItems.unpaidBillsTotal)}
    ${openSection('Investigate', report.openItems.investigate, report.openItems.investigateTotal)}
    ${openSection('Missing receipts', report.openItems.missingReceipts, report.openItems.missingReceiptsTotal)}

    ${heading('Transaction detail')}
    <div class="rtablewrap"><table class="rtable">
      <thead><tr>
        <th>Date</th><th>Description</th><th>Type</th><th>Classification</th>
        <th>Category / source</th><th>Method</th><th class="r">Amount</th><th>Receipt / status</th>
      </tr></thead>
      <tbody>${report.detail
        .map(
          (row) => `<tr>
          <td class="num">${row.date}</td>
          <td>${esc(row.description)}</td>
          <td>${esc(row.type)}</td>
          <td>${esc(row.classification)}</td>
          <td>${esc(row.categoryOrSource)}</td>
          <td>${esc(row.method)}</td>
          <td class="r num">${money2(row.amount)}</td>
          <td>${esc(row.receiptOrStatus)}</td>
        </tr>`,
        )
        .join('')}</tbody>
    </table></div>

    <p class="disclaimer">${esc(report.disclaimer)}</p>`
}
